// ---------------------------------------------------------------------------
// analytics.js — GA4 + Facebook Pixel
// ---------------------------------------------------------------------------

const state = {
  gaId: null,
  fbPixelId: null,
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function injectScript({ id, src, textContent }) {
  if (id && document.getElementById(id)) return;

  const script = document.createElement('script');
  if (id) script.id = id;
  if (src) {
    script.async = true;
    script.src = src;
  }
  if (textContent) script.textContent = textContent;

  document.head.appendChild(script);
}

function removeScript(id) {
  document.getElementById(id)?.remove();
}

// ── Google Analytics 4 ──────────────────────────────────────────────────────

export function initGA(id) {
  if (state.gaId === id) return;

  teardownGA();

  if (!id) return;

  state.gaId = id;

  // 1. Load gtag.js library
  injectScript({
    id: '__ga_lib',
    src: `https://www.googletagmanager.com/gtag/js?id=${id}`,
  });

  // 2. Configure gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', id);
}

export function teardownGA() {
  removeScript('__ga_lib');
  state.gaId = null;
}

export function isGAEnabled() {
  return state.gaId !== null;
}

/**
 * Track a page view in GA4.
 */
export function gaPageView(path, title) {
  if (!state.gaId || typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  });
}

/**
 * Track a custom GA4 event.
 */
export function gaEvent(action, params = {}) {
  if (!state.gaId || typeof window.gtag !== 'function') return;

  window.gtag('event', action, params);
}

// ── Facebook Pixel ──────────────────────────────────────────────────────────

export function initFBPixel(id) {
  if (state.fbPixelId === id) return;

  teardownFBPixel();

  if (!id) return;

  state.fbPixelId = id;

  injectScript({
    id: '__fb_pixel',
    textContent: `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${id}');
      fbq('track', 'PageView');
    `,
  });
}

export function teardownFBPixel() {
  removeScript('__fb_pixel');
  state.fbPixelId = null;
}

export function isFBPixelEnabled() {
  return state.fbPixelId !== null;
}

/**
 * Track a standard Facebook Pixel event.
 */
export function fbTrack(eventName, data = {}) {
  if (!state.fbPixelId) return;
  window.fbq('track', eventName, data);
}

/**
 * Track a custom Facebook Pixel event.
 */
export function fbTrackCustom(eventName, data = {}) {
  if (!state.fbPixelId) return;
  window.fbq('trackCustom', eventName, data);
}

// ── Combined tracking ───────────────────────────────────────────────────────

export function trackPageView(page, title = '') {
  if (isGAEnabled()) {
    gaPageView(page, title);
  }
  if (isFBPixelEnabled()) {
    fbTrack('PageView');
  }
}

export function reset() {
  teardownGA();
  teardownFBPixel();
}
